import { InMemoryCache } from '@apollo/client';
import type { FieldPolicy } from '@apollo/client/cache';
import { canonicalStringify } from '@apollo/client/utilities';

import type { TypedTypePolicies } from '../__generated__/apollo-helpers';
import generatedIntrospection from '../__generated__/fragment-matcher.json';
import { mergeTypePolicies } from '../__generated__/relay-pagination-policies';
import { mapRepositoryFragmentToId } from './repository.helpers';

/**
 * Page-scoped slots for a connection behind a grid that shows one page and
 * paints every edge the connection holds.
 *
 * The generated `relayStylePagination` accumulates instead: it stacks page 1
 * under page 2, and when no stored edge carries the `after` cursor it appends
 * the incoming page rather than replacing it. Cache keys include `__typename`,
 * so a commit going Queued -> Running -> Finished mid-pagination starts an
 * empty entity, after which every poll appends another copy of the page.
 *
 * Keying by `first`/`after` gives each page its own slot; `first` also keeps
 * the Summary's `repositories(first: 0)` tallies apart. A function rather than
 * the array form because Apollo drops `undefined` args from a specifier key,
 * and the page mounts without `where`/`orderBy` but sends explicit `null` once
 * the grid is interacted with.
 *
 * Other grids page their own connections through the same generated policy and
 * have the same defect; deferred.
 */
export const PAGED_REPOSITORIES_KEY_ARGS = [
  'orderBy',
  'where',
  'first',
  'after'
];

export const pagedRepositoriesKeyArgs: FieldPolicy['keyArgs'] = (args) =>
  canonicalStringify(
    Object.fromEntries(
      PAGED_REPOSITORIES_KEY_ARGS.map((name) => [name, args?.[name] ?? null])
    )
  );

const pagedRepositories = {
  fields: { repositories: { keyArgs: pagedRepositoriesKeyArgs } }
};

/**
 * Custom type policies that extend the auto-generated relay pagination policies.
 * These include keyFields definitions, custom read functions, and the per-field
 * pagination overrides above, where a grid needs a non-accumulating shape.
 *
 * The aggregate-vs-paginated cache-slot split and the canonical keyArgs
 * serialization are emitted into the generated policies by the relay-pagination
 * codegen plugin — see `codegen-plugins/relay-pagination/src/runtime.ts`.
 */
const customTypePolicies: TypedTypePolicies = {
  OrganizationCommitQueued: pagedRepositories,
  OrganizationCommitRunning: pagedRepositories,
  OrganizationCommitFinished: pagedRepositories,
  OrganizationCommitCanceled: pagedRepositories,
  OrganizationCommitError: pagedRepositories,
  // Message ids are stable across polls — the server emits the same id for a
  // message's IN_PROGRESS and COMPLETED snapshots, and later polls may return
  // fresher content for an already-seen id. Normalizing by id lets Apollo
  // dedupe the snapshot into one cache entity that updates in place.
  TextMessage: { keyFields: ['id'] },
  ErrorMessage: { keyFields: ['id'] },
  RecipeSearchMessage: { keyFields: ['id'] },
  RecipeRunMessage: { keyFields: ['id'] },
  RecipeOptionsMessage: { keyFields: ['id'] },
  DataTableSqlMessage: { keyFields: ['id'] },
  DataTablesMessage: { keyFields: ['id'] },
  Query: {
    fields: {
      organization: {
        read(_, { args, toReference }) {
          return toReference({
            __typename: 'Organization',
            id: args?.id
          });
        }
      }
    }
  },
  Commit: {
    keyFields: ['repository']
  },
  Recipe: {
    keyFields: false
  },
  // RecipeDescriptor represents an *instance* of a recipe — the same recipe id
  // can appear multiple times in a composite with different options. Default
  // normalization by id would collapse all instances into one cache entry,
  // so each rendered row would show the last instance's options.
  RecipeDescriptor: {
    keyFields: false
  },
  // No id, so it is stored inline on its visualization and the default write
  // *replaces* it. The visualizations list selects name/displayName; the selected card
  // lazily adds description/image/options. Without a merge, a list refetch drops them.
  VisualizationDescriptor: {
    merge: true
  },
  // No id either: the review query reads its message count and the board query
  // its messages, and each write would otherwise drop the other's fields.
  MessageBoard: {
    merge: true
  },
  // A `runs` slot per view's `first`; merge, as the id-less write otherwise
  // drops the other view's slot.
  DevCenter: {
    merge: true,
    fields: { runs: { keyArgs: pagedRepositoriesKeyArgs } }
  },
  RecipeArtifact: {
    keyFields: ['groupId', 'artifactId']
  },
  // RecipeBundle is now an interface - disable caching at interface level
  // and let concrete types handle their own cache keys
  RecipeBundle: {
    keyFields: false
  },
  // RecipeBundle concrete types - __typename is implicit in Apollo cache normalization
  // Use requestedVersion + version for proper cache key differentiation
  MavenRecipeBundle: {
    keyFields: ['groupId', 'artifactId', 'requestedVersion', 'version']
  },
  NpmRecipeBundle: {
    keyFields: ['packageName', 'requestedVersion', 'version']
  },
  YamlRecipeBundle: {
    keyFields: ['requestedVersion', 'version']
  },
  Repository: {
    keyFields: ['id'],
    fields: {
      id: {
        read(_, { readField }) {
          const __typename = readField('__typename') as string;
          const origin = readField('origin') as string;
          const path = readField('path') as string;
          const branch = readField('branch') as string;

          if (!origin || !path) {
            return null;
          }

          // mapRepositoryFragmentToId substitutes the empty-branch placeholder.
          return mapRepositoryFragmentToId({
            __typename: __typename as 'Repository',
            origin,
            path,
            branch
          });
        }
      }
    }
  }
};

/**
 * Merges auto-generated relay pagination policies with custom type policies.
 * The generated policies handle relay-style pagination for all Connection fields,
 * while custom policies add keyFields, read functions, and other cache configurations.
 */
export const typePolicies: TypedTypePolicies =
  mergeTypePolicies(customTypePolicies);

/**
 * The app's cache configuration. Exported so Storybook and tests exercise the
 * same normalization rules as production rather than a drifting copy.
 */
export const createApolloCache = (): InMemoryCache =>
  new InMemoryCache({
    possibleTypes: generatedIntrospection.possibleTypes,
    typePolicies
  });
